// src/services/auditService.js
import { db, auth } from '../config/firebase';
import { collection, addDoc, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';

class AuditService {
  constructor() {
    this.collectionName = 'audit_logs';
  }

  // Get client IP address (simplified for demo - in production use proper IP detection)
  async getClientIP() {
    try {
      // In production, you'd use a proper IP detection service
      // For now, return a placeholder that indicates IP should be captured
      return 'client_ip_detected';
    } catch (error) {
      console.warn('Could not detect client IP:', error);
      return 'ip_not_available';
    }
  }

  // Log authentication events (login, logout, etc.)
  async logAuthEvent(eventType, userId, userProfile, additionalData = {}) {
    if (!userId || !userProfile) {
      console.warn('Audit logging skipped - missing required data');
      return;
    }

    try {
      const tenantId = userProfile.clinicId || userProfile.tenantId;
      console.log('[audit] userProfile:', userProfile);
      console.log('[audit] tenantId extracted:', tenantId);
      console.log('[audit] writing to path:', `tenants/${tenantId}/audit`);
      
      if (!tenantId) {
        console.warn('Audit logging skipped - no tenant ID found');
        return;
      }


      const clientIP = await this.getClientIP();
      
      const auditEntry = {
        eventType, // 'login', 'logout', 'session_timeout', 'password_change', etc.
        userId,
        userRole: userProfile.role,
        tenantId,
        clinicId: tenantId, // For backward compatibility
        ipAddress: clientIP,
        userAgent: navigator.userAgent,
        timestamp: Timestamp.now(),
        deviceType: sessionStorage.getItem('deviceType') || 'unknown',
        sessionId: this.generateSessionId(),
        ...additionalData
      };

      // Store in tenant-scoped collection for data isolation
      const auditRef = collection(db, 'tenants', tenantId, this.collectionName);
      console.log('[audit] Firebase Auth currentUser:', auth.currentUser?.uid);
      console.log('[audit] About to write audit entry:', auditEntry);
      await addDoc(auditRef, auditEntry);

      console.log(`✅ Audit log recorded: ${eventType} for user ${userId} in tenant ${tenantId}`);
    } catch (error) {
      console.error('❌ Failed to log audit event:', error);
      // Don't throw error - audit logging failure shouldn't break user flow
    }
  }

  // Generate a simple session ID for tracking
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Log login event
  async logLogin(userId, userProfile, loginMethod = 'email_password') {
    await this.logAuthEvent('login', userId, userProfile, {
      loginMethod,
      rememberMe: sessionStorage.getItem('rememberMe') === 'true'
    });
  }

  // Log logout event
  async logLogout(userId, userProfile, logoutReason = 'user_initiated') {
    await this.logAuthEvent('logout', userId, userProfile, {
      logoutReason // 'user_initiated', 'session_timeout', 'admin_revoked', etc.
    });
  }

  // Log sensitive action
  async logSensitiveAction(userId, userProfile, actionType, actionDetails = {}) {
    await this.logAuthEvent('sensitive_action', userId, userProfile, {
      actionType, // 'data_export', 'patient_download', 'admin_config_change'
      actionDetails,
      requiresReauth: true
    });
  }

  // Log re-authentication event
  async logReAuthentication(userId, userProfile, reason = 'sensitive_action') {
    await this.logAuthEvent('re_authentication', userId, userProfile, {
      reason,
      previousSessionDuration: this.calculateSessionDuration()
    });
  }

  // Calculate session duration (simplified)
  calculateSessionDuration() {
    const loginTime = sessionStorage.getItem('loginTime');
    if (!loginTime) return 0;
    
    return Date.now() - parseInt(loginTime);
  }

  // Get audit logs for a tenant (admin function)
  async getAuditLogs(tenantId, filters = {}) {
    if (!db || !tenantId) return [];

    try {
      const auditRef = collection(db, 'tenants', tenantId, this.collectionName);
      let auditQuery = query(auditRef, orderBy('timestamp', 'desc'));

      // Apply filters
      if (filters.userId) {
        auditQuery = query(auditQuery, where('userId', '==', filters.userId));
      }
      if (filters.eventType) {
        auditQuery = query(auditQuery, where('eventType', '==', filters.eventType));
      }
      if (filters.limit) {
        auditQuery = query(auditQuery, limit(filters.limit));
      }

      const snapshot = await getDocs(auditQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() // Convert Firestore timestamp
      }));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  }

  // Get recent login attempts for security monitoring
  async getRecentLoginAttempts(tenantId, hours = 24) {
    const cutoffTime = Timestamp.fromDate(new Date(Date.now() - (hours * 60 * 60 * 1000)));
    
    return await this.getAuditLogs(tenantId, {
      eventType: 'login',
      limit: 100
    });
  }

  // Store login time for session duration tracking
  setLoginTime() {
    sessionStorage.setItem('loginTime', Date.now().toString());
  }

  // Clear session tracking data
  clearSessionData() {
    sessionStorage.removeItem('loginTime');
    sessionStorage.removeItem('rememberMe');
  }
}

// Create singleton instance
const auditService = new AuditService();

export default auditService;
